import {
  type MarketingMedia,
  approvedImage,
  approvedVideo,
  approvedVideoSource,
  posterForVideoSource,
} from '../marketing-media';

export type HomepageMedia = MarketingMedia;

export type HomepageAction =
  | { type: 'contact'; label: string; variant: 'cta' | 'secondary' }
  | {
      type: 'link';
      href: string;
      label: string;
      ariaLabel?: string;
      variant: 'cta' | 'secondary';
      external?: boolean;
      signedInAppletPath?: string;
      signedInHref?: string;
    }
  | {
      type: 'unavailable';
      label: string;
      reason: string;
      variant: 'cta' | 'secondary';
    };

export interface HomepageCardContent {
  actions: HomepageAction[];
  badge?: string;
  description: string;
  id: string;
  media: HomepageMedia;
  title: string;
}

export const heroMedia = {
  ...approvedVideo(
    'homepage-hero-v2-av1.webm',
    'Cartwheel homepage motion preview.',
  ),
  sources: [
    approvedVideoSource(
      'homepage-hero-v2-av1.webm',
      'video/webm; codecs="av01"',
    ),
    approvedVideoSource(
      'homepage-hero-v2-vp9.webm',
      'video/webm; codecs="vp9"',
    ),
    approvedVideoSource(
      'homepage-hero-v2.mp4',
      'video/mp4; codecs="avc1.64001f"',
    ),
  ],
};

export const models = [
  {
    id: 'comic',
    title: 'Comic',
    description:
      'Our family of foundation models for capturing motion from humans and objects to environments',
    media: {
      ...approvedVideo(
        '02_homepage_our-models_comic-card-v1.webm',
        'Comic model motion preview.',
      ),
      // The card-specific encode shares the existing poster with the full-size video.
      poster: posterForVideoSource('02_homepage_our-models_comic.webm'),
    },
    actions: [
      {
        type: 'link',
        href: '/model/comic',
        label: 'Learn more',
        ariaLabel: 'Learn more about Comic',
        variant: 'secondary',
      },
      {
        type: 'link',
        href: '/pricing',
        label: 'Try Comic',
        variant: 'cta',
        signedInAppletPath: '/comic',
      },
    ],
  },
  {
    id: 'swing',
    title: 'Swing',
    description:
      'Foundation models for generating & editing human motion, rapidly and at scale, from text or poses',
    media: approvedVideo(
      '03_homepage_our-models_swing.webm',
      'Swing model motion preview.',
    ),
    actions: [
      {
        type: 'link',
        href: '/model/swing',
        label: 'Learn more',
        ariaLabel: 'Learn more about Swing',
        variant: 'secondary',
      },
      {
        type: 'link',
        href: '/pricing',
        label: 'Try Swing',
        variant: 'cta',
        signedInAppletPath: '/generation',
      },
    ],
  },
] satisfies HomepageCardContent[];

export const modelProducts = [
  {
    id: 'capture',
    title: 'Performance Capture',
    description: 'The fastest and most powerful AI video motion capture',
    media: approvedVideo(
      '04_homepage_products_apps_performance-capture.webm',
      'Performance Capture preview.',
    ),
    actions: [
      {
        type: 'link',
        href: '/product/performance-capture',
        label: 'Learn more',
        ariaLabel: 'Learn more about Performance Capture',
        variant: 'secondary',
      },
      {
        type: 'link',
        href: '/pricing',
        label: 'Try it now',
        variant: 'cta',
        signedInAppletPath: '/comic',
      },
    ],
  },
  {
    id: 'generation',
    title: 'Performance Generation',
    description: 'Realistic human performance generation from text, at scale',
    media: approvedVideo(
      '05_homepage_products_apps_performance-generation.webm',
      'Performance Generation preview.',
    ),
    actions: [
      {
        type: 'link',
        href: '/product/performance-generation',
        label: 'Learn more',
        ariaLabel: 'Learn more about Performance Generation',
        variant: 'secondary',
      },
      {
        type: 'link',
        href: '/pricing',
        label: 'Try it now',
        variant: 'cta',
        signedInAppletPath: '/generation',
      },
    ],
  },
  {
    id: 'editor',
    title: 'Performance Editor',
    badge: 'Beta',
    description: 'Generatively edit performance with poses and paths',
    media: approvedVideo(
      '06_homepage_products_apps_performance-editor.webm',
      'Performance Editor preview.',
    ),
    actions: [
      {
        type: 'link',
        href: '/product/performance-editor',
        label: 'Learn more',
        ariaLabel: 'Learn more about Performance Editor',
        variant: 'secondary',
      },
      {
        type: 'link',
        href: '/pricing',
        label: 'Try it now',
        variant: 'cta',
        signedInAppletPath: '/motion-editor',
      },
    ],
  },
] satisfies HomepageCardContent[];

export const pipelineProducts = [
  {
    id: 'cartographer',
    title: 'Cartographer',
    description:
      'Universal motion retargeter to robots, 3D characters and other embodiments',
    media: approvedVideo(
      '07_homepage_products_pipeline-tools_cartographer.webm',
      'Cartographer preview.',
    ),
    actions: [
      {
        type: 'link',
        href: '/product/cartographer',
        label: 'Learn more',
        ariaLabel: 'Learn more about Cartographer',
        variant: 'secondary',
      },
      {
        type: 'link',
        href: '/pricing',
        label: 'Try it now',
        variant: 'cta',
        signedInAppletPath: '/cartographer',
      },
    ],
  },
  {
    id: 'mcp',
    title: 'MCP',
    description:
      'Have ChatGPT, Claude, or your agent capture motion, generate animation, or create rigged characters',
    media: approvedImage(
      '08_homepage_products_pipeline-tools_mcp.png',
      'Cartwheel MCP works with AI assistants and coding agents.',
    ),
    actions: [
      {
        type: 'link',
        href: 'https://github.com/Cartwhl/cartwheel-mcp',
        label: 'Try it now',
        variant: 'cta',
        external: true,
      },
    ],
  },
  {
    id: 'api',
    title: 'API',
    description: 'Scalable access for application and workflow integration',
    media: approvedVideo(
      '09_homepage_products_pipeline-tools_api.webm',
      'Cartwheel API preview.',
    ),
    actions: [
      {
        type: 'link',
        href: 'https://api-docs.getcartwheel.com/',
        label: 'Learn more',
        ariaLabel: 'Learn more about the Cartwheel API',
        variant: 'secondary',
        external: true,
      },
      {
        type: 'link',
        href: '/pricing',
        label: 'Try it now',
        variant: 'cta',
        signedInHref: '/profile/my-team',
      },
    ],
  },
  {
    id: 'plugins',
    title: 'Plugins',
    description: 'Leverage Cartwheel where you’re already working',
    media: approvedImage(
      '10_homepage_products_pipeline-tools_plugins.png',
      'Cartwheel plugins for creative tools.',
    ),
    actions: [
      {
        type: 'link',
        href: '/plugins',
        label: 'Download the plugins',
        variant: 'cta',
      },
    ],
  },
] satisfies HomepageCardContent[];

export const domainCards = [
  {
    id: 'robotics',
    title: 'Robotics & embodied AI',
    description:
      'Motion authoring, trajectories, and training data for how physical systems move.',
    media: approvedVideo(
      '11_homepage_domains_robotics.webm',
      'Robotics motion preview.',
    ),
  },
  {
    id: 'entertainment',
    title: 'Entertainment & media',
    description:
      'Games, film, TV, and advertising for production and realtime.',
    media: approvedVideo(
      '12_homepage_domains_entertainment-media.webm',
      'Entertainment and media motion preview.',
    ),
  },
  {
    id: 'athletics',
    title: 'Athletics',
    description: 'Performance, measured, analyzed, compared and tracked.',
    // This clip is outside the 30-file CDN delivery; its H.264 encode also avoids the prior VP9 decode error.
    media: {
      type: 'video',
      description: 'Athletics motion preview.',
      src: '/videos/homepage-athletics.mp4',
      mimeType: 'video/mp4',
      poster: posterForVideoSource('/videos/homepage-athletics.mp4'),
    },
  },
  {
    id: 'health',
    title: 'Health',
    description:
      'Movement as a diagnostic signal: gait, mobility, physical assessment, and rehabilitation.',
    media: {
      ...approvedVideo(
        '14_homepage_domains_health.webm',
        'Health movement preview.',
      ),
      // Chrome can intermittently fail to decode this WebM; keep a small H.264 fallback.
      fallbackSrc: '/videos/homepage-health.mp4',
    },
  },
] satisfies Omit<HomepageCardContent, 'actions'>[];
